import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';

import { DynamicThemeProvider } from './components/DynamicThemeProvider';
import Header from './components/layouts/Header';
import { useDynamicPageMeta } from './hooks/useDynamicPageMeta';
import { loadGoogleAnalytics } from './utils/googleTag';

import './App.css';
// Roboto fontsource
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

const Chatbot = React.lazy(() => import('./apps/chatbot'));
const Translator = React.lazy(() => import('./apps/translator'));

function SmartRedirect({ to }: { to: string }) {
  // verify if the language is saved in localStorage
  const savedLanguage = localStorage.getItem('i18nextLng');
  const defaultLanguage = 'en';

  // if savedLanguage is not set or not supported, use defaultLanguage
  const targetLanguage =
    savedLanguage && ['en', 'de'].includes(savedLanguage)
      ? savedLanguage
      : defaultLanguage;

  return <Navigate to={`${to}/${targetLanguage}`} replace />;
}

function LangManager() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    const supportedLngs = (i18n.options.supportedLngs as string[]) || ['en'];
    if (lang && supportedLngs.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }
  }, [lang, i18n]);

  return <Outlet />;
}

function AppLayout() {
  useDynamicPageMeta();

  return (
    <div>
      <Header />
      <div>
        <Outlet />
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    loadGoogleAnalytics();
  }, []);

  return (
    <DynamicThemeProvider>
      <CssBaseline />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Routes>
          <Route element={<LangManager />}>
            <Route element={<AppLayout />}>
              <Route path="/chatbot/:lang/*" element={<Chatbot />} />
              <Route path="/translator/*" element={<Translator />} />
            </Route>
          </Route>
          <Route path="/chatbot" element={<SmartRedirect to="/chatbot" />} />
        </Routes>
      </Suspense>
    </DynamicThemeProvider>
  );
}

export default App;
